import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  MapPin, Phone, Mail, Pencil, ExternalLink, Briefcase,
  Building2, HardHat, Save, ShieldCheck, Plus, X, Check,
} from "lucide-react";

// ─── Read-only contact row ────────────────────────────────────────────────────
function ContactRow({ role, name, phone, email }) {
  if (!name && !phone && !email) return null;
  return (
    <div className="py-2 border-b border-gray-100 last:border-0">
      <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-0.5">{role}</p>
      {name && <p className="text-sm font-medium text-gray-800">{name}</p>}
      <div className="flex flex-wrap gap-x-4 gap-y-0.5 mt-0.5">
        {phone && (
          <a href={`tel:${phone}`} className="flex items-center gap-1 text-xs text-blue-600 hover:underline">
            <Phone className="w-3 h-3" />{phone}
          </a>
        )}
        {email && (
          <a href={`mailto:${email}`} className="flex items-center gap-1 text-xs text-blue-600 hover:underline">
            <Mail className="w-3 h-3" />{email}
          </a>
        )}
      </div>
    </div>
  );
}

// ─── Edit row for a contact (name / phone / email) ────────────────────────────
function ContactEditRow({ label, prefix, form, onChange, onRemove }) {
  return (
    <div className="space-y-1 p-2 bg-gray-50 rounded-md border border-gray-200">
      <div className="flex items-center justify-between">
        <Label className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">{label}</Label>
        {onRemove && (
          <button onClick={onRemove} className="text-gray-400 hover:text-red-500 transition-colors p-0.5">
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      <div className="grid grid-cols-3 gap-2">
        <Input data-testid={`edit-${prefix}-name`} value={form[`${prefix}_name`] || ""} onChange={(e) => onChange(`${prefix}_name`, e.target.value)} placeholder="Name" className="h-7 text-xs" />
        <Input data-testid={`edit-${prefix}-phone`} value={form[`${prefix}_phone`] || ""} onChange={(e) => onChange(`${prefix}_phone`, e.target.value)} placeholder="Phone" className="h-7 text-xs" />
        <Input data-testid={`edit-${prefix}-email`} value={form[`${prefix}_email`] || ""} onChange={(e) => onChange(`${prefix}_email`, e.target.value)} placeholder="Email" className="h-7 text-xs" />
      </div>
    </div>
  );
}

// ─── Extra contact edit row (dynamic list item) ───────────────────────────────
function ExtraContactEditRow({ contact, onChange, onRemove }) {
  return (
    <div className="space-y-1 p-2 bg-gray-50 rounded-md border border-gray-200">
      <div className="flex items-center justify-between gap-2">
        <Input value={contact.role || ""} onChange={(e) => onChange({ ...contact, role: e.target.value })} placeholder="Role / Title" className="h-7 text-xs w-36" />
        <button onClick={onRemove} className="text-gray-400 hover:text-red-500 transition-colors p-0.5 shrink-0">
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
      <div className="grid grid-cols-3 gap-2">
        <Input value={contact.name || ""} onChange={(e) => onChange({ ...contact, name: e.target.value })} placeholder="Name" className="h-7 text-xs" />
        <Input value={contact.phone || ""} onChange={(e) => onChange({ ...contact, phone: e.target.value })} placeholder="Phone" className="h-7 text-xs" />
        <Input value={contact.email || ""} onChange={(e) => onChange({ ...contact, email: e.target.value })} placeholder="Email" className="h-7 text-xs" />
      </div>
    </div>
  );
}

// ─── Section card ─────────────────────────────────────────────────────────────
function SectionCard({ title, icon: Icon, onAdd, children }) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
      <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4 text-gray-500" />
          <span className="text-sm font-semibold text-gray-700">{title}</span>
        </div>
        {onAdd && (
          <button
            onClick={onAdd}
            data-testid={`add-contact-${title.toLowerCase().replace(/\s+/g, '-')}`}
            className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-medium transition-colors"
          >
            <Plus className="w-3.5 h-3.5" /> Add
          </button>
        )}
      </div>
      <div className="px-4 py-1 divide-y divide-gray-100">{children}</div>
    </div>
  );
}

// ─── ProjectInfoCard ──────────────────────────────────────────────────────────
export default function ProjectInfoCard({ project, onUpdated }) {
  const [isEditing, setIsEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [foremen, setForemen] = useState([]);
  const [pms, setPms] = useState([]);
  const [allInstallers, setAllInstallers] = useState([]);

  useEffect(() => {
    Promise.all([api.get("/installers"), api.get("/pms")]).then(([insR, pmsR]) => {
      const active = (insR.data || []).filter((i) => i.active);
      setAllInstallers(active);
      setForemen(active.filter((i) => ["Foreman", "Lead"].includes(i.role)));
      // PMs come from Settings → Users; their `active` flag is about sign-in, not assignability.
      setPms(pmsR.data || []);
    }).catch(() => {});
  }, []);

  const startEdit = () => {
    setForm({
      ...project,
      gc_extra_contacts: (project.gc_extra_contacts || []).map((c) => ({ ...c })),
      abs_extra_contacts: (project.abs_extra_contacts || []).map((c) => ({ ...c })),
    });
    setIsEditing(true);
  };

  const cancelEdit = () => setIsEditing(false);

  const set = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  // extra contacts helpers
  const addGcExtra = () =>
    setForm((f) => ({ ...f, gc_extra_contacts: [...(f.gc_extra_contacts || []), { role: "", name: "", phone: "", email: "" }] }));
  const addAbsExtra = () =>
    setForm((f) => ({ ...f, abs_extra_contacts: [...(f.abs_extra_contacts || []), { role: "", name: "", phone: "", email: "" }] }));
  const updateGcExtra = (i, val) =>
    setForm((f) => { const arr = [...(f.gc_extra_contacts || [])]; arr[i] = val; return { ...f, gc_extra_contacts: arr }; });
  const removeGcExtra = (i) =>
    setForm((f) => ({ ...f, gc_extra_contacts: (f.gc_extra_contacts || []).filter((_, idx) => idx !== i) }));
  const updateAbsExtra = (i, val) =>
    setForm((f) => { const arr = [...(f.abs_extra_contacts || [])]; arr[i] = val; return { ...f, abs_extra_contacts: arr }; });
  const removeAbsExtra = (i) =>
    setForm((f) => ({ ...f, abs_extra_contacts: (f.abs_extra_contacts || []).filter((_, idx) => idx !== i) }));

  const toggleGcInstaller = (id) => {
    const cur = new Set(form.default_gc_installer_request_ids || []);
    if (cur.has(id)) cur.delete(id); else cur.add(id);
    set("default_gc_installer_request_ids", [...cur]);
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.patch(`/projects/${project.id}`, form);
      toast.success("Job saved");
      onUpdated?.(r.data);
      setIsEditing(false);
    } catch {
      toast.error("Failed to save job");
    } finally {
      setSaving(false);
    }
  };

  // Resolve FK names for read-only display
  const foremanObj = foremen.find((f) => f.id === project.foreman_id);
  const pmObj = pms.find((p) => p.id === project.pm_id);
  const absPmDisplay = pmObj ? pmObj.name : (project.pm_name || null);
  const formForemanObj = foremen.find((f) => f.id === form.foreman_id);
  const formPmObj = pms.find((p) => p.id === form.pm_id);

  const fullAddress = [project.address, project.city, project.state, project.zip_code].filter(Boolean).join(", ");
  const mapsUrl = fullAddress
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(fullAddress)}`
    : null;

  const gcRequestedNames = (project.default_gc_installer_request_ids || [])
    .map((id) => allInstallers.find((i) => i.id === id)?.name).filter(Boolean);
  const gcFormSelectedNames = (form.default_gc_installer_request_ids || [])
    .map((id) => allInstallers.find((i) => i.id === id)?.name).filter(Boolean);

  const hasGcContacts = [project.gc_primary_name, project.gc_pm_name, project.gc_pe_name, project.gc_super_name]
    .concat((project.gc_extra_contacts || []).map((c) => c.name)).some(Boolean);
  const hasAbsContacts = [absPmDisplay, foremanObj?.name]
    .concat((project.abs_extra_contacts || []).map((c) => c.name)).some(Boolean);

  // ── READ-ONLY VIEW ────────────────────────────────────────────────────────
  if (!isEditing) {
    return (
      <div className="space-y-4" data-testid="project-info-card">
        {/* Header row */}
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-1.5">
            {fullAddress ? (
              <div className="flex items-center gap-1.5 text-sm text-gray-600">
                <MapPin className="w-4 h-4 text-gray-400 shrink-0" />
                <span>{fullAddress}</span>
                {mapsUrl && (
                  <a href={mapsUrl} target="_blank" rel="noopener noreferrer"
                    className="text-blue-500 hover:text-blue-700 ml-1" title="Open in Google Maps">
                    <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                )}
              </div>
            ) : (
              <p className="text-sm text-gray-400 italic">No address on file</p>
            )}
            <div className="flex flex-wrap gap-2 text-xs text-gray-500">
              {project.drawing_set && (
                <span data-testid="project-drawing-set">
                  Drawing Set: <span className="font-medium text-gray-700">{project.drawing_set}</span>
                </span>
              )}
              {project.budgeted_hours > 0 && (
                <span>Budgeted: <span className="font-medium text-gray-700">{project.budgeted_hours.toLocaleString()} hrs</span></span>
              )}
            </div>
          </div>
          <Button data-testid="edit-project-btn" variant="outline" size="sm" onClick={startEdit} className="flex items-center gap-1.5 shrink-0">
            <Pencil className="w-3.5 h-3.5" /> Edit
          </Button>
        </div>

        {/* Contact cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* GC Contacts */}
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-200 flex items-center gap-2">
              <HardHat className="w-4 h-4 text-gray-500" />
              <span className="text-sm font-semibold text-gray-700">GC Contacts</span>
            </div>
            <div className="px-4 divide-y divide-gray-100">
              {hasGcContacts ? (
                <>
                  <ContactRow role="Primary Contact" name={project.gc_primary_name} phone={project.gc_primary_phone} email={project.gc_primary_email} />
                  <ContactRow role="Project Manager (PM)" name={project.gc_pm_name} phone={project.gc_pm_phone} email={project.gc_pm_email} />
                  <ContactRow role="Project Engineer (PE)" name={project.gc_pe_name} phone={project.gc_pe_phone} email={project.gc_pe_email} />
                  <ContactRow role="Superintendent" name={project.gc_super_name} phone={project.gc_super_phone} email={project.gc_super_email} />
                  {(project.gc_extra_contacts || []).map((c, i) => (
                    <ContactRow key={i} role={c.role || "Contact"} name={c.name} phone={c.phone} email={c.email} />
                  ))}
                </>
              ) : (
                <p className="py-4 text-xs text-gray-400 text-center italic">No GC contacts — click Edit to add</p>
              )}
            </div>
          </div>

          {/* ABS Contacts */}
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-200 flex items-center gap-2">
              <Briefcase className="w-4 h-4 text-gray-500" />
              <span className="text-sm font-semibold text-gray-700">ABS Contacts</span>
            </div>
            <div className="px-4 divide-y divide-gray-100">
              {hasAbsContacts ? (
                <>
                  <ContactRow role="Project Manager (PM)" name={absPmDisplay} phone={pmObj?.phone} email={pmObj?.email} />
                  <ContactRow role="Foreman (FM)" name={foremanObj?.name} phone={foremanObj?.phone} email={foremanObj?.email} />
                  {(project.abs_extra_contacts || []).map((c, i) => (
                    <ContactRow key={i} role={c.role || "Contact"} name={c.name} phone={c.phone} email={c.email} />
                  ))}
                </>
              ) : (
                <p className="py-4 text-xs text-gray-400 text-center italic">No ABS contacts — click Edit to add</p>
              )}
            </div>
          </div>
        </div>

        {/* Requirements summary */}
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-200 flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-gray-500" />
            <span className="text-sm font-semibold text-gray-700">Job Requirements</span>
          </div>
          <div className="px-4 py-3 space-y-3">
            <div className="flex flex-wrap gap-2">
              <span
                className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full border ${
                  project.default_badged_required
                    ? "bg-amber-50 border-amber-200 text-amber-700"
                    : "bg-gray-50 border-gray-200 text-gray-500"
                }`}
                data-testid="req-badge-required"
              >
                <ShieldCheck className="w-3 h-3" />
                {project.default_badged_required ? "Badge Required" : "No Badge"}
              </span>
              <span
                className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full border ${
                  project.default_drug_test_required
                    ? "bg-red-50 border-red-200 text-red-700"
                    : "bg-gray-50 border-gray-200 text-gray-500"
                }`}
                data-testid="req-drug-test-required"
              >
                <ShieldCheck className="w-3 h-3" />
                {project.default_drug_test_required ? "Drug Test Required" : "No Drug Test"}
              </span>
            </div>
            <div>
              <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-1">GC Requested Installers</p>
              {gcRequestedNames.length > 0 ? (
                <div className="flex flex-wrap gap-1">
                  {gcRequestedNames.map((n) => (
                    <span key={n} className="px-2 py-0.5 bg-blue-50 border border-blue-200 text-blue-700 text-xs font-semibold rounded">{n}</span>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-gray-400 italic">None specified</p>
              )}
            </div>
            <div>
              <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-0.5">GC Notes</p>
              {project.default_gc_preference_notes ? (
                <p className="text-sm text-gray-700 whitespace-pre-line">{project.default_gc_preference_notes}</p>
              ) : (
                <p className="text-xs text-gray-400 italic">No notes</p>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── EDIT VIEW ─────────────────────────────────────────────────────────────
  return (
    <div className="space-y-5" data-testid="project-info-edit">

      {/* ── Project Details ── */}
      <SectionCard title="Job Details" icon={Building2}>
        <div className="py-3 grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <Label>Job Name</Label>
            <Input data-testid="edit-project-name" value={form.project_name || ""} onChange={(e) => set("project_name", e.target.value)} className="h-9" />
          </div>
          <div>
            <Label>Job Number</Label>
            <Input data-testid="edit-job-number" value={form.job_number || ""} onChange={(e) => set("job_number", e.target.value)} className="h-9" />
          </div>
          <div>
            <Label>Budgeted Hours</Label>
            <Input data-testid="edit-budgeted-hours" type="number" value={form.budgeted_hours || ""} onChange={(e) => set("budgeted_hours", parseFloat(e.target.value) || 0)} className="h-9" />
          </div>
          <div className="col-span-2">
            <Label>Drawing Set</Label>
            <Input data-testid="edit-drawing-set" value={form.drawing_set || ""} onChange={(e) => set("drawing_set", e.target.value)} className="h-9" placeholder="e.g., Rev 3 – 2024-11-15" />
          </div>
          <div className="col-span-2">
            <Label>Street Address</Label>
            <Input data-testid="edit-address" value={form.address || ""} onChange={(e) => set("address", e.target.value)} className="h-9" placeholder="123 Main St" />
          </div>
          <div>
            <Label>City</Label>
            <Input value={form.city || ""} onChange={(e) => set("city", e.target.value)} className="h-9" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label>State</Label>
              <Input value={form.state || ""} onChange={(e) => set("state", e.target.value)} className="h-9" placeholder="CO" />
            </div>
            <div>
              <Label>Zip</Label>
              <Input value={form.zip_code || ""} onChange={(e) => set("zip_code", e.target.value)} className="h-9" placeholder="80202" />
            </div>
          </div>
        </div>
      </SectionCard>

      {/* ── GC Contacts ── */}
      <SectionCard title="GC Contacts" icon={HardHat} onAdd={addGcExtra}>
        <div className="py-3 space-y-3">
          <ContactEditRow label="Project Manager (PM)" prefix="gc_pm" form={form} onChange={set} />
          <ContactEditRow label="Project Engineer (PE)" prefix="gc_pe" form={form} onChange={set} />
          <ContactEditRow label="Superintendent" prefix="gc_super" form={form} onChange={set} />
          <ContactEditRow label="Primary Contact (schedule emails)" prefix="gc_primary" form={form} onChange={set} />
          {(form.gc_extra_contacts || []).map((c, i) => (
            <ExtraContactEditRow
              key={i}
              contact={c}
              onChange={(val) => updateGcExtra(i, val)}
              onRemove={() => removeGcExtra(i)}
            />
          ))}
        </div>
      </SectionCard>

      {/* ── ABS Contacts ── */}
      <SectionCard title="ABS Contacts" icon={Briefcase} onAdd={addAbsExtra}>
        <div className="py-3 space-y-3">
          {/* PM dropdown */}
          <div className="space-y-1">
            <Label className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">Project Manager (PM)</Label>
            <select data-testid="edit-abs-pm" value={form.pm_id || ""} onChange={(e) => set("pm_id", e.target.value)}
              className="h-9 w-full text-sm border border-gray-300 rounded-md px-2 bg-white">
              <option value="">— Select PM —</option>
              {pms.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            {formPmObj && (
              <div className="flex gap-3 text-xs text-gray-500">
                {formPmObj.phone && <span className="flex items-center gap-0.5"><Phone className="w-3 h-3" />{formPmObj.phone}</span>}
                {formPmObj.email && <span className="flex items-center gap-0.5"><Mail className="w-3 h-3" />{formPmObj.email}</span>}
              </div>
            )}
          </div>
          {/* FM dropdown */}
          <div className="space-y-1">
            <Label className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">Foreman (FM)</Label>
            <select data-testid="edit-abs-fm" value={form.foreman_id || ""} onChange={(e) => set("foreman_id", e.target.value)}
              className="h-9 w-full text-sm border border-gray-300 rounded-md px-2 bg-white">
              <option value="">— Select Foreman —</option>
              {foremen.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
            </select>
            {formForemanObj && (
              <div className="flex gap-3 text-xs text-gray-500">
                {formForemanObj.phone && <span className="flex items-center gap-0.5"><Phone className="w-3 h-3" />{formForemanObj.phone}</span>}
                {formForemanObj.email && <span className="flex items-center gap-0.5"><Mail className="w-3 h-3" />{formForemanObj.email}</span>}
              </div>
            )}
          </div>
          {/* Extra ABS contacts */}
          {(form.abs_extra_contacts || []).map((c, i) => (
            <ExtraContactEditRow
              key={i}
              contact={c}
              onChange={(val) => updateAbsExtra(i, val)}
              onRemove={() => removeAbsExtra(i)}
            />
          ))}
        </div>
      </SectionCard>

      {/* ── Requirements ── */}
      <SectionCard title="Requirements" icon={ShieldCheck}>
        <div className="py-3 space-y-4">
          <div className="flex gap-6">
            <label className="flex items-center gap-2 cursor-pointer text-sm" data-testid="edit-badged-required">
              <input type="checkbox" checked={!!form.default_badged_required} onChange={(e) => set("default_badged_required", e.target.checked)} className="rounded" />
              Badge Required
            </label>
            <label className="flex items-center gap-2 cursor-pointer text-sm" data-testid="edit-drug-test-required">
              <input type="checkbox" checked={!!form.default_drug_test_required} onChange={(e) => set("default_drug_test_required", e.target.checked)} className="rounded" />
              Drug Test Required
            </label>
          </div>
          <div>
            <Label>GC Installer Request</Label>
            <p className="text-[11px] text-gray-500 mb-1">Installer(s) the GC has specifically requested.</p>
            <div className="border border-gray-200 rounded-md bg-white p-2 max-h-32 overflow-y-auto grid grid-cols-2 gap-0.5" data-testid="gc-installer-request-list">
              {allInstallers.map((inst) => {
                const checked = (form.default_gc_installer_request_ids || []).includes(inst.id);
                return (
                  <label key={inst.id} className="flex items-center gap-1.5 text-xs cursor-pointer hover:bg-gray-50 rounded px-1 py-0.5">
                    <input type="checkbox" checked={checked} onChange={() => toggleGcInstaller(inst.id)} />
                    <span className="truncate">{inst.name}</span>
                  </label>
                );
              })}
            </div>
            {gcFormSelectedNames.length > 0 && (
              <div className="mt-1.5 flex flex-wrap gap-1">
                {gcFormSelectedNames.map((n) => (
                  <span key={n} className="px-1.5 py-0.5 bg-blue-50 border border-blue-200 text-blue-700 text-[10px] font-semibold rounded">{n}</span>
                ))}
              </div>
            )}
          </div>
          <div>
            <Label>GC Notes</Label>
            <Textarea data-testid="edit-gc-notes" rows={3} value={form.default_gc_preference_notes || ""}
              onChange={(e) => set("default_gc_preference_notes", e.target.value)}
              placeholder="e.g., GC prefers Catarino lead; escort required past 2pm." />
          </div>
        </div>
      </SectionCard>

      {/* Save / Cancel */}
      <div className="flex justify-end gap-2 pt-1">
        <Button variant="outline" onClick={cancelEdit}>Cancel</Button>
        <Button data-testid="save-project-btn" onClick={save} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white">
          <Save className="w-3.5 h-3.5 mr-1" />
          {saving ? "Saving…" : "Save Job"}
        </Button>
      </div>
    </div>
  );
}
